import { INSTALL_PROGRESS_STEPS } from '@/constants/catalog';
import { onCefEvent } from '@/utils/cefEvents';
import { createInstallSteps } from '@/utils/install';
import { track } from '@/utils/track';

// 安装事件只订阅一次（发起安装的入口有多个，都会调 registerInstallCefListener）
let installCefListenerRegistered = false;

type InstallMode = 'oneClick' | 'aipptOnly' | null;
type AipptInstallStatus = 'download' | 'install' | 'success' | 'failed';

type AipptInstallPayload = {
    status: AipptInstallStatus;
    progress: number;
    eventName: string;
    msg?: any;
    message?: string;
};

type AipptOnlyInstallOptions = {
    onProgress?: (payload: AipptInstallPayload) => void;
    onSuccess?: (payload: AipptInstallPayload) => void;
    onError?: (payload: AipptInstallPayload) => void;
};

export const useInstallStore = defineStore('InstallStore', {
    state: () => ({
        // 全局 CEF 安装事件只有一个入口，用 installMode 区分当前安装场景。
        installMode: null as InstallMode,

        // 一键安装弹层的进度状态。
        installModalVisible: false,
        installTimer: null,
        installSteps: createInstallSteps(INSTALL_PROGRESS_STEPS),
        installProgressLabel: '准备中…',
        installProgressPct: '0%',
        installProgressWidth: '0%',
        installSubLabel: '',
        installDoneVisible: false,
        installCloseVisible: false,

        // 安装信息
        installInfo: null,
        officeInstallInfo: null,
        aipptInstallInfo: null,
        aipptInstallStarted: false,
        aipptOnlyInstallCallbacks: null as AipptOnlyInstallOptions | null,
    }),

    getters: {},

    actions: {
        resetInstallProgress() {
            if (this.installTimer) {
                clearTimeout(this.installTimer);
                this.installTimer = null;
            }

            this.installModalVisible = true;
            this.installSteps = createInstallSteps(INSTALL_PROGRESS_STEPS);
            this.installProgressLabel = '准备中…';
            this.installProgressPct = '0%';
            this.installProgressWidth = '0%';
            this.installSubLabel = '';
            this.installDoneVisible = false;
            this.installCloseVisible = false;
            this.installInfo = null;
            this.officeInstallInfo = null;
            this.aipptInstallInfo = null;
            this.aipptInstallStarted = false;
        },

        registerInstallCefListener() {
            // 每次发起安装都会调一次，订阅一次就够了，不然事件会被重复处理
            if (installCefListenerRegistered) return;
            installCefListenerRegistered = true;

            onCefEvent((msgType: any, msg: any) => {
                this.handleInstallCefEvent(msgType, msg);
            });
        },

        updateInstallProgress(percent, label = '', subLabel = '') {
            const normalizedPercent = Math.max(0, Math.min(100, percent));

            if (label) {
                this.installProgressLabel = label;
            }

            this.installProgressPct = `${normalizedPercent}%`;
            this.installProgressWidth = `${normalizedPercent}%`;
            this.installSubLabel = subLabel;
        },

        activateInstallStep(stepIndex, percent, subLabel = '') {
            const step = this.installSteps[stepIndex];
            if (!step) return;

            step.status = 'active';
            step.currentIcon = step.icon;
            this.updateInstallProgress(percent, step.text, subLabel);
        },

        completeInstallStep(stepIndex) {
            const step = this.installSteps[stepIndex];
            if (!step) return;

            step.status = 'completed';
            step.currentIcon = '✅';
            step.detailText = step.detail;
        },

        failInstallStep(stepIndex) {
            const step = this.installSteps[stepIndex];
            if (!step) return;

            step.status = 'failed';
            step.currentIcon = '✕';
            step.detailText = '下载失败，请稍后重试';
            this.installProgressLabel = '下载失败';
            this.installSubLabel = '';
            this.installDoneVisible = false;
            this.installCloseVisible = true;
            this.installTimer = null;
        },

        finishInstallProgress() {
            this.completeInstallStep(1);
            this.installProgressLabel = '安装完成';
            this.installProgressPct = '100%';
            this.installProgressWidth = '100%';
            this.installSubLabel = '';
            this.installDoneVisible = true;
            this.installCloseVisible = true;
            this.installTimer = null;
        },

        async startAipptInstall() {
            if (this.aipptInstallStarted) return;

            if (!this.aipptInstallInfo) {
                this.failInstallStep(1);
                this.installMode = null;
                return;
            }

            const appStore = useAppStore();

            this.aipptInstallStarted = true;
            this.completeInstallStep(0);
            this.activateInstallStep(1, 55, this.installSteps[1]?.subSteps?.[0]?.label || '');

            try {
                await appStore.MyWin.WorkAiApi("client_download_install", {
                    ...this.aipptInstallInfo,
                    install_type: 0
                });
            } catch (err) {
                console.error("Aippt下载安装调用失败", err);
                this.failInstallStep(1);
                this.installMode = null;
            }
        },

        // CEF 通过 WorkAiSetData 推送下载安装阶段，前端只负责映射到已有进度 UI。
        handleInstallCefEvent(eventName, msg = null) {
            console.log("安装流程CEF消息", eventName, msg);

            if (this.installMode === 'aipptOnly') {
                this.handleAipptOnlyCefEvent(eventName, msg);
                return;
            }

            switch (eventName) {
                case 'client_download_office_begin':
                    this.activateInstallStep(0, 8, this.installSteps[0]?.subSteps?.[0]?.label || '');
                    break;
                case 'client_download_office_finshed_success': {
                    const downloadSubSteps = this.installSteps[0]?.subSteps || [];
                    const downloadDoneLabel = downloadSubSteps[downloadSubSteps.length - 1]?.label || '';

                    this.completeInstallStep(0);
                    this.updateInstallProgress(50, this.installSteps[0]?.text || '', downloadDoneLabel);
                    void this.startAipptInstall();
                    break;
                }
                case 'client_download_office_finshed_failed':
                    this.failInstallStep(0);
                    this.installMode = null;
                    break;
                case 'client_download_install_begin':
                    this.completeInstallStep(0);
                    this.activateInstallStep(1, 60, this.installSteps[1]?.subSteps?.[0]?.label || '');
                    break;
                case 'client_download_install_finshed_success': {
                    const installSubSteps = this.installSteps[1]?.subSteps || [];
                    const installDownloadDoneLabel = installSubSteps[installSubSteps.length - 1]?.label || '';

                    this.completeInstallStep(0);
                    this.activateInstallStep(1, 75, installDownloadDoneLabel);
                    break;
                }
                case 'client_download_install_finshed_failed':
                    this.failInstallStep(1);
                    this.installMode = null;
                    break;
                case 'client_download_install_install_begin':
                    this.completeInstallStep(0);
                    this.activateInstallStep(1, 85, this.installSteps[1]?.subSteps?.[1]?.label || '');
                    break;
                case 'client_download_install_install_finshed':
                    this.finishInstallProgress();
                    // 一键安装的 aippt 装完也要落插件记录：这条链路不走 usePluginInstall 的回调，
                    // 不补的话「已安装插件」页和插件按钮要等下次启动检测才更新
                    void usePluginsMineStore().MARK_AipptInstalled();
                    this.installMode = null;
                    break;
                default:
                    break;
            }
        },

        notifyAipptOnlyProgress(status: AipptInstallStatus, progress: number, eventName: string, msg: any = null, message = '') {
            this.aipptOnlyInstallCallbacks?.onProgress?.({
                status,
                progress,
                eventName,
                msg,
                message,
            });
        },

        finishAipptOnlyInstall(eventName: string, msg: any = null) {
            const payload = {
                status: 'success' as AipptInstallStatus,
                progress: 100,
                eventName,
                msg,
                message: '安装完成',
            };

            this.aipptOnlyInstallCallbacks?.onSuccess?.(payload);
            this.installMode = null;
            this.aipptOnlyInstallCallbacks = null;
            this.aipptInstallStarted = false;
        },

        failAipptOnlyInstall(message: string, eventName = 'client_download_install_failed', msg: any = null) {
            const payload = {
                status: 'failed' as AipptInstallStatus,
                progress: 0,
                eventName,
                msg,
                message,
            };

            this.aipptOnlyInstallCallbacks?.onError?.(payload);
            this.installMode = null;
            this.aipptOnlyInstallCallbacks = null;
            this.aipptInstallStarted = false;
        },

        handleAipptOnlyCefEvent(eventName, msg = null) {
            // 与一键安装(oneClick)一致，按 CEF 事件阶段推进「固定进度值」；
            // 客户端推送的是阶段事件而非实时百分比，若去解析 msg 里的进度会解析不到而停在 0。
            switch (eventName) {
                case 'client_download_install_begin':
                    // 开始下载
                    this.notifyAipptOnlyProgress('download', 15, eventName, msg);
                    break;
                case 'client_download_install_finshed_success':
                    // 下载完成
                    this.notifyAipptOnlyProgress('download', 60, eventName, msg);
                    break;
                case 'client_download_install_finshed_failed':
                    this.failAipptOnlyInstall('Aippt 下载失败，请稍后重试', eventName, msg);
                    break;
                case 'client_download_install_install_begin':
                    // 开始安装
                    this.notifyAipptOnlyProgress('install', 85, eventName, msg);
                    break;
                case 'client_download_install_install_finshed':
                    // 安装完成
                    this.finishAipptOnlyInstall(eventName, msg);
                    break;
                default:
                    // 其它未知事件不更新进度，避免把已推进的进度打回 0
                    break;
            }
        },

        // 安装 AiPPT 不需要登录：未登录也能直接装
        async startAipptOnlyInstall(options: AipptOnlyInstallOptions = {}) {
            const appStore = useAppStore();

            if (this.installMode) {
                options.onError?.({
                    status: 'failed',
                    progress: 0,
                    eventName: 'install_busy',
                    message: '当前已有安装任务进行中，请稍后再试',
                });
                return false;
            }

            this.installMode = 'aipptOnly';
            this.aipptOnlyInstallCallbacks = options;
            this.installInfo = null;
            this.officeInstallInfo = null;
            this.aipptInstallInfo = null;
            this.aipptInstallStarted = false;
            this.notifyAipptOnlyProgress('download', 0, 'install_ready', null, '准备下载 Aippt');
            this.registerInstallCefListener();

            try {
                // 刷新云控配置（同时写入 AppStore.AppConfig），取 Aippt 下载包路径
                this.installInfo = await appStore.FETCH_AppConfig();

                if (!this.installInfo) {
                    this.failAipptOnlyInstall('获取 Aippt 安装配置失败，请稍后重试', 'config_failed');
                    return false;
                }

                this.aipptInstallInfo = this.installInfo?.list?.find(item => item.name == "Aippt");

                if (!this.aipptInstallInfo) {
                    this.failAipptOnlyInstall('未获取到 Aippt 安装包，请稍后重试', 'config_missing_aippt', this.installInfo);
                    return false;
                }

                this.aipptInstallStarted = true;
                await appStore.MyWin.WorkAiApi("client_download_install", {
                    ...this.aipptInstallInfo,
                    install_type: 0
                });
                return true;
            } catch (err) {
                console.error("Aippt单独下载安装调用失败", err);
                this.failAipptOnlyInstall('Aippt 下载安装调用失败，请稍后重试', 'install_exception', err);
                return false;
            }
        },

        // 一键安装通过步骤状态机驱动进度条与步骤列表联动。
        // 不需要登录：未登录也能直接装
        async startOneClickInstall() {
            const appStore = useAppStore();

            // 点击一键安装office+aippt埋点（不 await：上报失败或变慢不能挡住安装流程）
            track("installOfficeAippt");

            if (this.installModalVisible || this.installMode) return;

            this.installMode = 'oneClick';
            this.resetInstallProgress();
            this.registerInstallCefListener();

            try {
                // 刷新云控配置（同时写入 AppStore.AppConfig），取下载包路径
                this.installInfo = await appStore.FETCH_AppConfig();

                if (!this.installInfo) {
                    this.failInstallStep(0);
                    this.installMode = null;
                    return;
                }

                console.log("下载包路径==>", this.installInfo);

                this.officeInstallInfo = this.installInfo.list.find(item => item.name == "office");
                this.aipptInstallInfo = this.installInfo.list.find(item => item.name == "Aippt");

                if (!this.officeInstallInfo || !this.aipptInstallInfo) {
                    this.failInstallStep(0);
                    this.installMode = null;
                    return;
                }

                // 安装office
                await appStore.MyWin.WorkAiApi("client_download_office_install", this.officeInstallInfo.install);
            } catch (err) {
                console.error("下载安装调用失败", err);
                this.failInstallStep(0);
                this.installMode = null;
            }
        },

        // 关闭安装弹层时同步清理当前定时器。
        closeInstallModal() {
            this.installModalVisible = false;
            if (this.installTimer) {
                clearTimeout(this.installTimer);
                this.installTimer = null;
            }
        },
    },
});
